import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import axios from 'axios'

import { clearTokens, tokenStorage } from './token'

interface AxiosPendingRequest {
    type: 'axios'
    originalRequest: AxiosRequestConfig
    resolve: (value: AxiosResponse) => void
    reject: (error: unknown) => void
}

/**
 * 文件上传请求队列项
 */
interface UploadPendingRequest {
    type: 'upload'
    itemId: string
    resolve: () => void
    reject: (error: Error) => void
    retryFn?: () => Promise<void>
}

type PendingRequest = AxiosPendingRequest | UploadPendingRequest

interface TokenRefreshManagerConfig {
    apiBaseUrl: string
    refreshEndpoint: string
    redirectLoginFn: () => void
    httpInstance: any
}

/**
 * 统一的 Token 刷新管理器
 *
 * 负责处理 Axios 请求和文件上传的 401 错误，统一管理 token 刷新逻辑
 * 避免并发刷新，维护请求队列
 */
export class TokenRefreshManager {
    private isRefreshing = false

    private refreshPromise: Promise<string | null> | null = null

    private pendingQueue: PendingRequest[] = []

    private readonly apiBaseUrl: string

    private readonly refreshEndpoint: string

    private readonly redirectLoginFn: () => void

    private readonly httpInstance: any

    constructor(config: TokenRefreshManagerConfig) {
        this.apiBaseUrl = config.apiBaseUrl
        this.refreshEndpoint = config.refreshEndpoint
        this.redirectLoginFn = config.redirectLoginFn
        this.httpInstance = config.httpInstance
    }

    /**
     * 处理 Axios 请求的 401 错误
     * @param originalRequest 原始请求配置
     * @param httpInstance axios 实例
     * @returns 刷新后重试的响应
     */
    async handleAxios401(originalRequest: AxiosRequestConfig, httpInstance: any): Promise<AxiosResponse> {
        // 防止无限循环重试
        if ((originalRequest as any)._retry) {
            throw new Error('Token refresh already attempted')
        }
        ;(originalRequest as any)._retry = true

        // 如果正在刷新，加入队列等待
        if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
                this.enqueueAxiosRequest(originalRequest, resolve, reject)
            })
        }

        // 开始刷新
        const token = await this.startRefresh()

        if (token) {
            if (!originalRequest.headers) {
                originalRequest.headers = {}
            }
            originalRequest.headers['Authorization'] = `Bearer ${token}`

            // 标记此请求为重试请求，跳过 request 拦截器的业务处理
            originalRequest.headers['X-Skip-Interceptor'] = 'true'

            // 关键修复：禁用 transformRequest，因为 data 已经被转换过了
            // 避免重试时对已序列化的数据再次进行转换
            originalRequest.transformRequest = []

            return httpInstance(originalRequest)
        }
        throw new Error('Token refresh failed')
    }

    /**
     * 处理文件上传的 401 错误
     * @param itemId 上传项 ID（可选，用于日志和调试）
     * @param retryFn 重试函数（可选，token 刷新成功后自动调用）
     * @returns Promise，刷新完成后 resolve
     */
    async handleUpload401(itemId?: string, retryFn?: () => Promise<void>): Promise<void> {
        // 如果正在刷新，加入队列等待
        if (this.isRefreshing) {
            return new Promise<void>((resolve, reject) => {
                this.enqueueUploadRequest(itemId || '', resolve, reject, retryFn)
            })
        }

        // 开始刷新
        const token = await this.startRefresh()
        if (!token) {
            throw new Error('Token refresh failed')
        }

        // 如果提供了重试函数，执行它
        if (retryFn) {
            await retryFn()
        }
    }

    /**
     * 开始 token 刷新流程
     * @returns 新的 access token
     */
    private async startRefresh(): Promise<string | null> {
        if (this.isRefreshing && this.refreshPromise) {
            return this.refreshPromise
        }

        this.isRefreshing = true
        this.refreshPromise = this.doRefresh()

        try {
            const token = await this.refreshPromise
            await this.processQueue(token)
            return token
        } catch (error) {
            await this.processQueue(null)
            throw error
        } finally {
            this.isRefreshing = false
            this.refreshPromise = null
        }
    }

    /**
     * 执行实际的 token 刷新请求
     * @returns 新的 access token
     */
    private async doRefresh(): Promise<string | null> {
        try {
            const refreshToken = tokenStorage.getRefreshToken()
            if (!refreshToken) {
                throw new Error('No refresh token available')
            }

            // 使用原生 axios，不走拦截器避免循环
            const resp = await axios.post(
                this.apiBaseUrl + this.refreshEndpoint,
                { refreshToken },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'B-Origin-Domain': window.location.hostname
                        // 'B-Origin-Domain': ''
                    }
                }
            )

            if (resp.status !== 200) {
                throw new Error('Refresh request failed')
            }

            const { code, content } = resp.data
            if (code !== '0000000') {
                throw new Error('Refresh response code error')
            }

            const { accessToken: newAccessToken } = content
            if (!newAccessToken) {
                throw new Error('No access token in response')
            }

            // 存储新 token
            tokenStorage.setAccessToken(newAccessToken)
            return newAccessToken
        } catch (error) {
            // 刷新失败，清除所有 token 并跳转登录
            clearTokens()
            this.redirectLoginFn()
            throw error
        }
    }

    /**
     * 处理等待队列中的所有请求
     * @param newToken 新的 access token，null 表示刷新失败
     */
    private async processQueue(newToken: string | null): Promise<void> {
        const promises = this.pendingQueue.map(async request => {
            try {
                if (request.type === 'axios') {
                    if (newToken) {
                        if (!request.originalRequest.headers) {
                            request.originalRequest.headers = {}
                        }
                        request.originalRequest.headers['Authorization'] = `Bearer ${newToken}`

                        // 标记此请求为重试请求，跳过 request 拦截器的业务处理
                        request.originalRequest.headers['X-Skip-Interceptor'] = 'true'

                        // 关键修复：禁用 transformRequest，因为 data 已经被转换过了
                        request.originalRequest.transformRequest = []
                        const result = await this.httpInstance(request.originalRequest)
                        request.resolve(result)
                    } else {
                        request.reject(new Error('Token refresh failed'))
                    }
                } else if (request.type === 'upload') {
                    if (newToken) {
                        // 如果有重试函数，执行它
                        if (request.retryFn) {
                            await request.retryFn()
                        }
                        request.resolve()
                    } else {
                        request.reject(new Error('Token refresh failed'))
                    }
                }
            } catch (error) {
                request.reject(error as Error)
            }
        })

        this.pendingQueue = []
        await Promise.allSettled(promises)
    }

    /**
     * 将 Axios 请求加入等待队列
     */
    private enqueueAxiosRequest(
        originalRequest: AxiosRequestConfig,
        resolve: (value: AxiosResponse) => void,
        reject: (error: unknown) => void
    ): void {
        this.pendingQueue.push({
            type: 'axios',
            originalRequest,
            resolve,
            reject
        })
    }

    /**
     * 将文件上传请求加入等待队列
     */
    private enqueueUploadRequest(itemId: string, resolve: () => void, reject: (error: Error) => void, retryFn?: () => Promise<void>): void {
        this.pendingQueue.push({
            type: 'upload',
            itemId,
            resolve,
            reject,
            retryFn
        })
    }

    /**
     * 获取当前是否正在刷新
     */
    getIsRefreshing(): boolean {
        return this.isRefreshing
    }
}
